/**
 * Lohnbuchungsbeleg — client-side BC clipboard builder.
 *
 * Turns the derived booking entries (buildJournal) into a tab-separated table matching the
 * Dynamics BC "Fibu Erfassungsjournale" grid, driven entirely by the configurable column
 * layout (BcExportConfig). Pasting into BC is COLUMN-POSITION based, so column order/content
 * comes from the saved layout. Pure & client-side, mirroring lohnJournal.ts (no server call).
 */

import { buildJournal } from './lohnJournal';
import type {
    AccountVatDto, BcExportColumn, BcExportConfig, ComputedVoucher, JournalEntry, RunMeta,
} from '../types/lohnBuchungsbeleg';

/**
 * 9000-Regel (bestätigt, hardcodiert): Kostenstellen 9100 / 9200 / 9300 werden in BC als
 * Cost Center Code 9000 + Business Unit Code = <original KST> geführt. Alle anderen KST landen
 * 1:1 im Cost Center Code, Business Unit bleibt leer. (Ändert sich das BC-Schema, hier anpassen.)
 */
const BU_BASE = '9000';
const BU_SPLIT_CODES = new Set(['9100', '9200', '9300']);

export function mapCostCenterToBc(code: string): { costCenter: string; businessUnit: string } {
    const c = (code ?? '').trim();
    return BU_SPLIT_CODES.has(c)
        ? { costCenter: BU_BASE, businessUnit: c }
        : { costCenter: c, businessUnit: '' };
}

/** Swiss (19'507.00) or plain (19507.00), 2 decimals, '-' for negatives. Blank at 0 when blankZero. */
function fmtBcNumber(n: number, format: 'swiss' | 'plain', blankZero: boolean): string {
    if (blankZero && Math.abs(n) < 0.005) return '';
    const neg = n < 0;
    const [int, dec] = Math.abs(n).toFixed(2).split('.');
    const grouped = format === 'swiss' ? int.replace(/\B(?=(\d{3})+(?!\d))/g, "'") : int;
    return `${neg ? '-' : ''}${grouped}.${dec}`;
}

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
function lastDayOfMonth(year: number, month1: number): number {
    if (month1 === 2 && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)) return 29;
    return DAYS_IN_MONTH[month1 - 1] ?? 28;
}

/**
 * Booking date: run valutaDate ('YYYY-MM-DD') or, if missing, the last day of the period
 * ('YYYY-MM'). Formatted by string substitution (dd/MM/yyyy) — no Date/toISOString, so no
 * timezone shift (CET/CEST) can move the day.
 */
function bookingDate(meta: Pick<RunMeta, 'valutaDate' | 'period'>, dateFormat: string): string {
    let y: number, m: number, d: number;
    if (meta.valutaDate && /^\d{4}-\d{2}-\d{2}$/.test(meta.valutaDate)) {
        [y, m, d] = meta.valutaDate.split('-').map(Number);
    } else {
        const [yy, mm] = (meta.period || '').split('-').map(Number);
        y = yy || 0; m = mm || 1; d = lastDayOfMonth(y, m);
    }
    return dateFormat
        .replace('dd', String(d).padStart(2, '0'))
        .replace('MM', String(m).padStart(2, '0'))
        .replace('yyyy', String(y).padStart(4, '0'));
}

function resolveCell(
    entry: JournalEntry,
    col: BcExportColumn,
    date: string,
    format: 'swiss' | 'plain',
    vatByAccount: Map<string, AccountVatDto>,
): string {
    const betrag = entry.soll - entry.haben;
    switch (col.source) {
        case 'buchungsdatum': return date;
        case 'account':       return entry.account;
        case 'text':          return entry.text;
        case 'betrag':        return fmtBcNumber(betrag, format, false);
        case 'betragMw':      return fmtBcNumber(betrag, format, false);
        case 'soll':          return fmtBcNumber(entry.soll, format, true);
        case 'haben':         return fmtBcNumber(entry.haben, format, true);
        case 'costCenter':    return mapCostCenterToBc(entry.costCenter).costCenter;
        case 'businessUnit':  return mapCostCenterToBc(entry.costCenter).businessUnit;
        case 'mwstGeschaeft': return vatByAccount.get(entry.account)?.mwstGeschaeft ?? '';
        case 'mwstProdukt':   return vatByAccount.get(entry.account)?.mwstProdukt ?? '';
        case 'const':         return col.value ?? '';
        case 'blank':         return '';
        default:              return '';
    }
}

/** The BC table as arrays (for preview + TSV). Only enabled columns are included. */
export function buildBcRows(
    voucher: ComputedVoucher,
    config: BcExportConfig,
    meta: Pick<RunMeta, 'valutaDate' | 'period'>,
    vatAccounts: AccountVatDto[] = [],
): { header: string[]; rows: string[][] } {
    const entries = buildJournal(voucher).entries;
    const cols = config.columns.filter(c => c.enabled);
    const date = bookingDate(meta, config.dateFormat);
    const vatByAccount = new Map(vatAccounts.map(v => [v.account, v]));
    return {
        header: cols.map(c => c.label),
        rows: entries.map(e => cols.map(c => resolveCell(e, c, date, config.numberFormat, vatByAccount))),
    };
}

/**
 * Σ Soll − Σ Haben in Rappen *as pasted* (each entry rounded to 2 dp like fmtBcNumber). BC rejects
 * or mis-books a journal that does not balance at this level, even when the 4-dp nets do.
 */
export function bcPasteDifferenceRappen(voucher: ComputedVoucher): number {
    let diff = 0;
    for (const e of buildJournal(voucher).entries) diff += Math.round(e.soll * 100) - Math.round(e.haben * 100);
    return diff;
}

/** Tab-separated table (CRLF rows) ready for the clipboard / BC paste. */
export function buildBcClipboard(
    voucher: ComputedVoucher,
    config: BcExportConfig,
    meta: Pick<RunMeta, 'valutaDate' | 'period'>,
    vatAccounts: AccountVatDto[] = [],
): string {
    const { header, rows } = buildBcRows(voucher, config, meta, vatAccounts);
    const lines = config.includeHeader ? [header, ...rows] : rows;
    return lines.map(r => r.join('\t')).join('\r\n');
}

/**
 * Write text to the clipboard, with an execCommand fallback for older/insecure contexts.
 * Returns true on success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // fall through to the legacy path
    }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        ta.remove();
        return ok;
    } catch {
        return false;
    }
}
